import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SidebarPanel } from "../../components/SidebarPanel";
import { Button } from "../../components/Button";
import { formatDate, formatPeriod } from "../../utils/dateUtils";
import { formatMoney } from "../../utils/formatMoney";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { useMarkStoreInvoiceAsSentMutation } from "../../query/invoicesQuery";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import type { RestInvoiceData } from "@KassaroAB/shared/types/invoices/storeInvoice";
import type { StoreId } from "@KassaroAB/shared/types/ids";

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${fontSizes.small}px;

  th {
    text-align: left;
    padding: ${spacing.x1}px 0;
    color: ${colors.textMuted};
    font-weight: 500;
    border-bottom: 1px solid ${colors.border};
  }

  td {
    padding: ${spacing.x1}px 0;
    border-bottom: 1px solid ${colors.border};
  }
`;

const InvoiceTotal = styled.div`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

export const StoreInvoiceResultPanel = ({
  invoice,
  storeId,
  onClose,
  onInvoiceUpdated,
}: {
  invoice: RestInvoiceData;
  storeId?: StoreId;
  onClose: () => void;
  onInvoiceUpdated?: (invoice: RestInvoiceData) => void;
}) => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const { mutate: markAsSent, isPending: isMarkingAsSent, error: markAsSentError } = useMarkStoreInvoiceAsSentMutation(storeId);
  const lineItems = invoice.lineItems ?? [];
  const isSent = invoice.sent ?? false;

  const handleMarkAsSent = () => {
    markAsSent(invoice.period, {
      onSuccess: (data) => onInvoiceUpdated?.(data),
    });
  };

  return (
    <SidebarPanel isOpen onClose={onClose} title={t("invoice.panelTitle")}>
      <div style={{ display: "flex", flexDirection: "column", gap: spacing.x3 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: spacing.x1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: fontSizes.small, color: colors.textMuted }}>
            <span>{t("invoice.period")}</span>
            <span style={{ color: colors.text, fontWeight: 500 }}>
              {formatPeriod(invoice.period)}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: fontSizes.small, color: colors.textMuted }}>
            <span>{t("invoice.generatedAt")}</span>
            <span style={{ color: colors.text, fontWeight: 500 }}>{formatDate(invoice.generatedAt)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: fontSizes.small, color: colors.textMuted }}>
            <span>{t("invoice.sent")}</span>
            <span style={{ color: colors.text, fontWeight: 500 }}>{isSent ? t("common.yes") : t("common.no")}</span>
          </div>
        </div>
        {storeId && !isSent && isAdmin && (
          <Button
            onClick={handleMarkAsSent}
            disabled={isMarkingAsSent}
          >
            {isMarkingAsSent ? t("invoice.markingAsSent") : t("invoice.markAsSent")}
          </Button>
        )}
        {markAsSentError && (
          <div style={{ color: colors.danger }}>{markAsSentError.message}</div>
        )}
        {lineItems.length > 0 ? (
          <>
            <InvoiceTable>
              <thead>
                <tr>
                  <th>{t("invoice.lineItem.name")}</th>
                  <th>{t("invoice.lineItem.type")}</th>
                  <th>{t("invoice.lineItem.period")}</th>
                  <th>{t("invoice.lineItem.months")}</th>
                  <th>{t("invoice.lineItem.unitPrice")}</th>
                  <th>{t("invoice.lineItem.total")}</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, lineItemIndex) => (
                  <tr key={`${item.billingItemId}-${item.startDate}-${item.segmentStartDate ?? "none"}-${lineItemIndex}`}>
                    <td>{item.name}</td>
                    <td>{t(`billingItems.type.${item.type}`)}</td>
                    <td>{formatDate(item.startDate)} – {formatDate(item.endDate)}</td>
                    <td>{(Math.round(item.months * 1000) / 1000).toFixed(3).replace(/\.?0+$/, "")}</td>
                    <td>{formatMoney(item.unitPrice)}</td>
                    <td>{formatMoney(item.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </InvoiceTable>
            <InvoiceTotal>
              {t("invoice.total")}: {invoice.totalAmount ? formatMoney(invoice.totalAmount) : "–"}
            </InvoiceTotal>
          </>
        ) : (
          <p style={{ margin: 0, color: colors.textMuted }}>{t("invoice.empty")}</p>
        )}
      </div>
    </SidebarPanel>
  );
};
